// components/elements/cards/collectionCard/CollectionCard.tsx
"use client";
import { Collection } from "@/app/lib/features/collection/collection.types";
import { Dot, Share2, MoreHorizontal, Edit, Trash2 } from "lucide-react";
import Link from "next/link";
import FallbackImage from "../../FallbackImage";
import { Button } from "@/app/components/shadcn/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/shadcn/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/shadcn/dropdown-menu";
import { useState, useCallback } from "react";
import useCollection from "@/app/lib/features/collection/collection.hook";
import { bumpFollowersCount } from "@/app/lib/features/collection/collection.utils";
import { cn } from "@/app/utils/cn";
import { trackInteraction } from "@/app/lib/tracking/interactions";

interface CollectionCardProps {
  collection: Collection;
  variant?: "default" | "management";
  isMine?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onFollowToggle?: (collectionId: number) => Promise<void>;
}

const GRID_SIZE = 3;

const CollectionCard = ({
  collection,
  variant = "default",
  isMine = false,
  onEdit,
  onDelete,
  onFollowToggle,
}: CollectionCardProps) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isTogglingFollow, setIsTogglingFollow] = useState(false);

  const { toggleFollow } = useCollection();

  // Yerel optimistic state: bu kart hem Redux'a bağlı listelerden (Koleksiyonlarım,
  // Takip Ettiklerim — collection.slice zaten follow toggle'da güncelliyor) hem de
  // ana sayfadaki koleksiyon carousel'inden (home slice, collection.slice'ın hiç
  // dokunmadığı ayrı bir kaynak) besleniyor. Local state ikisinde de butonun ve
  // takipçi sayısının anında ve doğru güncellenmesini garanti eder; prop'un
  // arkasındaki Redux dilimi güncellensin ya da güncellenmesin fark etmez.
  // Liste yeniden çekilip kart id bazlı key ile remount olduğunda taze sunucu
  // verisiyle sıfırdan başlar (aşağıdaki useState lazy initializer).
  const [followState, setFollowState] = useState(() => ({
    isFollowed:
      collection.is_followed ??
      !!collection.button_list?.some((btn) => btn.action === "unfollow"),
    count: collection.followers_count,
    text: collection.followers_count_text,
  }));
  const isFollowed = followState.isFollowed;

  const handleFollowToggle = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsTogglingFollow(true);
      trackInteraction("section", collection.id, "follow");

      const success = onFollowToggle
        ? await onFollowToggle(collection.id)
            .then(() => true)
            .catch(() => false)
        : await toggleFollow(collection.id);

      if (success) {
        setFollowState((prev) => {
          const nextIsFollowed = !prev.isFollowed;
          const { count, text } = bumpFollowersCount(
            prev.count,
            prev.text,
            nextIsFollowed ? 1 : -1,
          );
          return { isFollowed: nextIsFollowed, count, text };
        });
      }
      setIsTogglingFollow(false);
    },
    [collection.id, onFollowToggle, toggleFollow],
  );

  // API'den gelen Collection objesi doğrudan kullanılıyor
  const collectionName = collection.name;
  const collectionLink = `/koleksiyonlar/${collection.collectionUUID}`;
  const userName = collection.customer_full_name;
  const userAvatar = collection.customer_photo;

  // Ürün resimlerini items'dan çıkar
  const subImages = collection.items
    .slice(0, GRID_SIZE)
    .map((item) => item.product?.first_image)
    .filter((img): img is string => Boolean(img));

  const productCount = collection.items_count_text;
  const followerCount = followState.text;
  const viewCount = collection.views_count_text;

  const iconEye = collection.icon_eye;
  const iconBookmark = collection.icon_bookmark;
  const iconDots = collection.icon_dots;
  const shareDropdown = collection.share_dropdown;
  const buttonList = collection.button_list || [];

  const editButton = buttonList.find((b) =>
    b.title.toLowerCase().includes("düzenle"),
  );
  const deleteButton = buttonList.find((b) =>
    b.title.toLowerCase().includes("sil"),
  );

  const gridSlots = Array.from({ length: GRID_SIZE }, (_, i) => ({
    image: subImages[i] || null,
    index: i,
  }));

  const handleShareClick = async (
    item: { type: "copy" | "share"; url: string; title: string },
    index: number,
  ) => {
    if (item.type === "copy") {
      try {
        await navigator.clipboard.writeText(item.url);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      } catch {
        // silent
      }
    } else {
      window.open(item.url, "_blank");
      setIsShareOpen(false);
    }
  };

  const handleNativeShare = async () => {
    const shareUrl =
      shareDropdown?.items.find((item) => item.type === "copy")?.url ||
      `${window.location.origin}${collectionLink}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: collectionName, url: shareUrl });
      } catch {
        // silent
      }
    } else {
      await navigator.clipboard.writeText(shareUrl).catch(() => null);
    }
  };

  return (
    <div className="group relative bg-white rounded-lg border border-gray-200 hover:border-main hover:shadow-md transition-all duration-300 ease-out overflow-visible">
      {variant === "management" && (
        <div
          className="absolute top-3 right-3 z-30"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="flex items-center gap-2">
            {shareDropdown && (
              <>
                <div className="hidden md:block">
                  <Popover open={isShareOpen} onOpenChange={setIsShareOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        className="bg-white hover:bg-gray-50 shadow-sm"
                      >
                        {shareDropdown.icon ? (
                          <div className="relative w-4 h-4">
                            <FallbackImage
                              src={shareDropdown.icon}
                              alt="Paylaş"
                              fill
                              className="object-contain"
                              sizes="16px"
                            />
                          </div>
                        ) : (
                          <Share2 className="w-4 h-4" />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-56 p-2"
                      align="end"
                      side="bottom"
                      sideOffset={8}
                      onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                      <div className="space-y-1">
                        <div className="px-3 py-2 text-sm font-medium text-gray-900 border-b">
                          {shareDropdown.text}
                        </div>
                        {shareDropdown.items.map((item, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleShareClick(item, index)}
                            className="cursor-pointer w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors text-left"
                          >
                            <div className="relative w-4 h-4 shrink-0">
                              <FallbackImage
                                src={item.icon}
                                alt={item.title}
                                fill
                                className="object-contain"
                                sizes="16px"
                              />
                            </div>
                            <span>
                              {item.type === "copy" && copiedIndex === index
                                ? "Kopyalandı!"
                                : item.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="block md:hidden">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    className="bg-white hover:bg-gray-50 shadow-sm"
                    onClick={handleNativeShare}
                  >
                    {shareDropdown.icon ? (
                      <div className="relative w-4 h-4">
                        <FallbackImage
                          src={shareDropdown.icon}
                          alt="Paylaş"
                          fill
                          className="object-contain"
                          sizes="16px"
                        />
                      </div>
                    ) : (
                      <Share2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </>
            )}

            {isMine && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    className="bg-white hover:bg-gray-50 shadow-sm"
                  >
                    {iconDots ? (
                      <div className="relative w-4 h-4">
                        <FallbackImage
                          src={iconDots}
                          alt="Seçenekler"
                          fill
                          className="object-contain"
                          sizes="16px"
                        />
                      </div>
                    ) : (
                      <MoreHorizontal className="w-4 h-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit?.();
                    }}
                  >
                    {editButton?.icon ? (
                      <div className="relative w-4 h-4 mr-2 shrink-0">
                        <FallbackImage
                          src={editButton.icon}
                          alt={editButton.title}
                          fill
                          className="object-contain"
                          sizes="16px"
                        />
                      </div>
                    ) : (
                      <Edit className="w-4 h-4 mr-2" />
                    )}
                    {editButton?.title || "Koleksiyonu Düzenle"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.();
                    }}
                    className="text-red-600"
                  >
                    {deleteButton?.icon ? (
                      <div className="relative w-4 h-4 mr-2 shrink-0">
                        <FallbackImage
                          src={deleteButton.icon}
                          alt={deleteButton.title}
                          fill
                          className="object-contain"
                          sizes="16px"
                        />
                      </div>
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    {deleteButton?.title || "Koleksiyonu Sil"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}

      <Link
        href={collectionLink}
        className="block"
        onClick={() => trackInteraction("section", collection.id, "click")}
      >
        <div className="p-3 sm:p-4">
          {/* variant="management" iken paylaş/seçenekler butonları bu satırın
              üstünde absolute duruyor (top-3 right-3) ve kullanıcı adını
              kesiyordu. İki buton + boşluk ~72px, o kadar yer ayrılıyor. */}
          <div
            className={cn(
              "flex items-center gap-2 mb-3",
              variant === "management" && "pr-18",
            )}
          >
            <div className="relative w-8 h-8 sm:w-10 sm:h-10 shrink-0">
              <FallbackImage
                src={userAvatar}
                alt={userName}
                fill
                className="object-cover rounded-full border border-[#ffecdf]"
                sizes="40px"
                quality={70}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                {userName}
              </p>
              <p className="text-xs text-muted-foreground line-clamp-2 leading-tight">
                {collectionName}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1 sm:gap-2 mb-3 h-32 sm:h-48 rounded-lg overflow-hidden border-2 border-gray-200">
            {gridSlots.map(({ image, index }) =>
              image ? (
                <div
                  key={index}
                  className="relative w-full h-full overflow-hidden bg-gray-100"
                >
                  <FallbackImage
                    src={image}
                    alt={`${collectionName} - ${index + 1}`}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    fallbackClassName="object-contain p-2 opacity-40"
                    sizes="(max-width: 640px) 25vw, (max-width: 1024px) 20vw, 15vw"
                    quality={70}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div
                  key={index}
                  className="w-full h-full bg-gray-100 animate-pulse"
                />
              ),
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground">
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-medium">{productCount}</span>
                <Dot className="w-3 h-3" />
                <div className="flex items-center gap-1">
                  <span>{followerCount}</span>
                  {iconBookmark && (
                    <div className="relative w-3 h-3">
                      <FallbackImage
                        src={iconBookmark}
                        alt="Takipçi"
                        fill
                        className="object-contain"
                        sizes="16px"
                      />
                    </div>
                  )}
                </div>
                <Dot className="w-3 h-3" />
                <div className="flex items-center gap-1">
                  <span>{viewCount}</span>
                  {iconEye && (
                    <div className="relative w-3 h-3">
                      <FallbackImage
                        src={iconEye}
                        alt="Görüntülenme"
                        fill
                        className="object-contain"
                        sizes="16px"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!collection.is_collection_auth && (
              <Button
                type="button"
                size="sm"
                variant={isFollowed ? "default" : "outline"}
                className="h-7 text-xs shrink-0"
                disabled={isTogglingFollow}
                onClick={handleFollowToggle}
              >
                {isFollowed ? "Takip Ediliyor" : "Takip Et"}
              </Button>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default CollectionCard;
